'use client'

import React, { FC, PropsWithChildren, useRef } from 'react'
import {
  useRadio,
  AriaRadioProps,
  VisuallyHidden,
  useFocusRing
} from 'react-aria'
import { useRadioGroupContext } from './radio-group'

interface RadioProps extends AriaRadioProps {
  className?: string
}

/** @public */
export const Radio: FC<PropsWithChildren<RadioProps>> = props => {
  const { children, className } = props
  const state = useRadioGroupContext()
  const inputRef = useRef<HTMLInputElement>(null)
  const { inputProps } = useRadio(props, state, inputRef)

  return (
    <label className={className}>
      <input {...inputProps} ref={inputRef} />
      {children}
    </label>
  )
}

export interface RadioRenderProps {
  isFocused: boolean
  isSelected: boolean
}

interface CustomRadioProps extends AriaRadioProps {
  render: FC<RadioRenderProps>
  className?: string
  label: string
}

export const CustomRadio: FC<CustomRadioProps> = props => {
  const { className, render } = props
  const state = useRadioGroupContext()
  const inputRef = useRef<HTMLInputElement>(null)
  const { inputProps, isSelected } = useRadio(props, state, inputRef)
  const { isFocusVisible, focusProps } = useFocusRing()

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    inputRef.current?.closest('label')?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    })
    focusProps.onFocus?.(e)
  }

  return (
    <label className={className} role="radio" aria-checked={isSelected}>
      <VisuallyHidden>
        <input
          {...inputProps}
          {...focusProps}
          onFocus={handleFocus}
          ref={inputRef}
        />
      </VisuallyHidden>
      {render({ isFocused: isFocusVisible, isSelected })}
    </label>
  )
}
